import { GEO_BOUNDARIES } from '@/constants/endpoints';
import { MAP_SCALES } from '@/constants/geo';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useRef, useState } from 'react';
import MapGl, { Layer, Source } from 'react-map-gl';
import { dimensionLabel, num } from '../format';
import { COLORS } from '../theme';
import MapHoverTooltip from './MapHoverTooltip';
import MapLegend from './MapLegend';
import WidgetSkeleton from './WidgetSkeleton';
import { buildFillPaint } from './geoFillLayer';
import { MARKET_FOCUSED_ZOOM, computeDensityFocusedViewState } from './mapViewport';

const VITE_MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
// Same default camera as the legacy Mapbox maps (constants/geo.js
// INITIAL_VIEW_STATE), minus its width/height fields — sizing here comes
// from the container Box, and react-map-gl's ViewState type doesn't have them.
const INITIAL_VIEW_STATE = { latitude: 38, longitude: -97, zoom: 3, bearing: 0, pitch: 0 };

/**
 * Market (DMA) map — a real Mapbox GL choropleth, the same library and boundary
 * data the legacy Simplifi/GAM reporting tab already ships in production
 * (components/Map/GeoMap.jsx), not the chartjs-chart-geo bubble map this
 * replaced.
 *
 * Why the switch: Earl asked for zoom/pan. chartjs-plugin-zoom doesn't
 * support chartjs-chart-geo's custom projection/size scales — verified live,
 * its own `.zoom()` API silently no-ops — so there was no way to add zoom to
 * the bubble map without hand-rolling pan/zoom from scratch. Mapbox GL has
 * wheel-zoom, drag-pan, and pinch built in for free.
 *
 * Trade-off, accepted explicitly: this renders as a filled choropleth like
 * legacy, not bubbles. A large-area DMA reads as more prominent regardless of
 * its actual delivery volume — the exact readability problem bubbles were
 * originally chosen to avoid. Mapbox's own renderer doesn't have the d3
 * polygon-winding inversion that ruled out a filled DMA choropleth before.
 */
interface DmaFeature {
  type: 'Feature';
  properties: { CODE?: string; NAME?: string; name?: string; value?: number | null };
  geometry: unknown;
}

interface IProps {
  rows: ReportRow[];
  labelKey?: string;
  valueKey?: string;
  codeKey?: string;
  metricLabel?: string;
  formatValue?: (v: number) => string;
}

let cachedBoundaries: DmaFeature[] | null = null;

const DmaChoropleth = ({
  rows,
  labelKey = 'geo_dma',
  valueKey = 'impressions',
  codeKey = 'geo_dma_code',
  metricLabel = 'Impressions',
  formatValue = num,
}: IProps) => {
  const [boundaries, setBoundaries] = useState<DmaFeature[] | null>(cachedBoundaries);
  const [failed, setFailed] = useState(false);
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  const [hover, setHover] = useState<{ x: number; y: number; label: string; value: number } | null>(null);
  // "Follow legacy" (Josh, Unified Reporting readiness review, #0000): zoom to
  // the densest market on load instead of the fixed default view. Only runs
  // once per mount so it doesn't fight the user's own pan/zoom afterward.
  // Uses MARKET_FOCUSED_ZOOM, not the City/ZIP constant — a DMA polygon spans
  // a whole metro region, and the tighter zoom filled the viewport with one
  // solid market shape and no visible boundaries (live QA, flank#4748 only
  // specs auto-zoom for City/ZIP).
  const hasFocusedRef = useRef(false);

  useEffect(() => {
    if (cachedBoundaries) {
      setBoundaries(cachedBoundaries);
      return;
    }
    const controller = new AbortController();
    // Empty geoIds = every DMA, so a metro is placeable whether or not it
    // delivered in the current window. Same ~210 shapes every time, so it's
    // fetched once and cached at module scope.
    axios
      .post(GEO_BOUNDARIES, { mapScale: MAP_SCALES.DMA, geoIds: [] }, { signal: controller.signal })
      .then(({ data }) => {
        cachedBoundaries = (data?.features ?? []) as DmaFeature[];
        setBoundaries(cachedBoundaries);
      })
      .catch(error => {
        if (!axios.isCancel(error)) setFailed(true);
      });
    return () => controller.abort();
  }, []);

  // Not memoized, unlike City/Zip's `features` — Dma has no re-centre-on-
  // filter-change requirement to guard against (hasFocusedRef below is a
  // one-shot flag, not a per-dataset tracker), so a fresh array each render
  // is harmless here.
  const valueByCode = new Map<string, number>();
  for (const row of rows) {
    // The unattributed bucket isn't a place, and it would skew the colour scale.
    if (dimensionLabel(row[labelKey]) === 'Unmapped') continue;
    const code = String(row[codeKey] ?? '').trim();
    const value = Number(row[valueKey] ?? 0);
    if (code && Number.isFinite(value) && value > 0) valueByCode.set(code, value);
  }
  const features: DmaFeature[] = (boundaries ?? []).map(f => {
    const code = String(f.properties?.CODE ?? '').trim();
    return { ...f, properties: { ...f.properties, value: valueByCode.get(code) ?? null } };
  });

  // Code review, #0000: was a render-phase `if (...) { ref.current = true;
  // setViewState(...) }` — the ref flip is a side effect, and running it
  // during the render phase meant a discarded render (StrictMode
  // double-invoke, an interrupted concurrent render) could flip the flag
  // without its setViewState ever committing, silently eating the one-shot
  // auto-focus. Declared before the early returns below (Rules of Hooks).
  useEffect(() => {
    if (!hasFocusedRef.current && features.length > 0) {
      hasFocusedRef.current = true;
      setViewState(computeDensityFocusedViewState(features, INITIAL_VIEW_STATE, MARKET_FOCUSED_ZOOM));
    }
  }, [features]);

  if (failed) {
    return (
      <Typography variant='body2' sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
        Couldn't load the market boundaries. The ranked list below still reflects the data.
      </Typography>
    );
  }
  if (!boundaries) return <WidgetSkeleton height={360} />;

  const scaleMax = Math.max(0, ...Array.from(valueByCode.values()));
  const geojson = { type: 'FeatureCollection' as const, features };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ height: 360, width: '100%', position: 'relative' }}>
        <MapGl
          {...viewState}
          style={{ width: '100%', height: '100%' }}
          onMove={e => setViewState(e.viewState)}
          mapStyle='mapbox://styles/mapbox/light-v9'
          mapboxAccessToken={VITE_MAPBOX_TOKEN}
          interactiveLayerIds={['data']}
          onMouseMove={e => {
            const feature = e.features?.[0] as DmaFeature | undefined;
            const value = feature?.properties?.value;
            if (!feature || value == null) {
              setHover(null);
              return;
            }
            setHover({
              x: e.point.x,
              y: e.point.y,
              label: feature.properties?.NAME ?? feature.properties?.name ?? '',
              value,
            });
          }}
          onMouseLeave={() => setHover(null)}
        >
          <Source type='geojson' data={geojson}>
            <Layer id='data' type='fill' paint={buildFillPaint(features) as any} />
          </Source>
        </MapGl>
        {hover && (
          <MapHoverTooltip
            x={hover.x}
            y={hover.y}
            label={hover.label}
            metricLabel={metricLabel}
            value={formatValue(hover.value)}
          />
        )}
      </Box>
      <MapLegend max={scaleMax} />
    </Box>
  );
};

export default DmaChoropleth;
